/**
 * F-14: IndexedDB schema-versioning safety net.
 *
 * idb-keyval is a flat key-value store with no built-in migration support.
 * If we ship a code change that renames a key, changes a payload shape,
 * or splits one cache into two, old clients that come back online would
 * read stale data into the new code and either crash or render garbage.
 *
 * Pattern:
 *   - Bump CURRENT_SCHEMA_VERSION whenever a cached payload shape or key
 *     name changes incompatibly.
 *   - On app boot, call ensureIdbSchemaCompatible() once. If the stored
 *     version mismatches CURRENT_SCHEMA_VERSION, every Fahryo-managed key
 *     is wiped — data is then re-fetched from Supabase / re-derived on
 *     next use.
 *   - Compatible no-op when version matches (fast path).
 *
 * What is NOT solved here:
 *   - Multi-device write conflict resolution (CRDT). That's a separate
 *     audit finding (F-14b in the backlog). This file just prevents
 *     stale-cache-vs-new-code corruption — a much simpler property
 *     than concurrent-edit reconciliation.
 *
 * Note on backwards compat:
 *   - Existing prod users currently have no version key. On first
 *     deploy of this code they'll hit "no version" → wipe → re-fetch
 *     from Supabase on next online action. Acceptable: the only thing
 *     truly lost is the offline-question-cache and pending sync-queue
 *     entries that haven't been flushed yet.
 */
import { get, set, del, keys } from 'idb-keyval';
import logger from './logger';

const SCHEMA_VERSION_KEY = 'fahryo_idb_schema_version';

/**
 * Bump this on every backwards-incompatible cache change.
 *
 * v1 (2026-05-26): Initial versioned schema. Captures the post-F-13
 *   state where question cache stores only the active-language column.
 */
const CURRENT_SCHEMA_VERSION = 1;

/**
 * Keys this app writes to IDB. Used as the wipe-target list on a schema
 * mismatch — we deliberately enumerate instead of `clear()` so a future
 * caller (e.g. an admin debug tool) that stashes its own keys isn't
 * silently nuked.
 */
const KNOWN_KEYS = [
  'fahryo_app_user',
  'fahryo_app_session',
  'fahryo_app_questions',
  'fahryo_sync_queue',
  'fahryo_app_essences',
  // Add new keys here as features land. Forgetting to add a key just
  // means it survives the wipe — annoying but not corrupting.
];

let initialized = false;

export async function ensureIdbSchemaCompatible(): Promise<void> {
  if (initialized) return; // idempotent — safe to call from multiple init paths
  initialized = true;

  try {
    const stored = (await get(SCHEMA_VERSION_KEY)) as number | undefined;
    if (stored === CURRENT_SCHEMA_VERSION) {
      return; // fast path: schema matches, nothing to do
    }

    if (stored === undefined) {
      // First run on this device after this helper landed. Stamp the
      // current version without wiping — existing v0 caches are
      // structurally compatible with the current code.
      await set(SCHEMA_VERSION_KEY, CURRENT_SCHEMA_VERSION);
      logger.info('IDB', `Stamped schema version ${CURRENT_SCHEMA_VERSION} (first run)`);
      return;
    }

    // Stored version != current version → wipe Fahryo keys and re-stamp.
    // We list known keys + intersect with what actually exists so we
    // don't pay for missing-key deletes.
    logger.info(
      'IDB',
      `Schema mismatch (stored=${stored}, current=${CURRENT_SCHEMA_VERSION}) — wiping caches`
    );
    const existing = await keys();
    const existingStr = new Set(
      existing.filter((k) => typeof k === 'string').map((k) => String(k))
    );
    await Promise.all(
      KNOWN_KEYS.filter((k) => existingStr.has(k)).map((k) => del(k).catch(() => undefined))
    );
    await set(SCHEMA_VERSION_KEY, CURRENT_SCHEMA_VERSION);
  } catch (err) {
    // IDB is fragile in private mode / Safari edge cases. We never want
    // schema-versioning to block app startup — log and move on.
    logger.warn('IDB', 'Schema-version check failed:', err);
  }
}

/** Exported for tests. */
export const __SCHEMA_INTERNALS__ = {
  SCHEMA_VERSION_KEY,
  CURRENT_SCHEMA_VERSION,
  KNOWN_KEYS,
};
